import { useState } from "react";
import { History, Loader2, MessageSquare, Phone, Users as UsersIcon, ArrowRightLeft } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ErrorState, LoadingState } from "@/components/common/States";
import { useWorkspace } from "@/context/WorkspaceContext";
import { useFetch } from "@/lib/hooks";
import { ACTIVITY_TYPES } from "@/lib/resources";
import { errorMessage, fmtDateTime } from "@/lib/api";

const ICONS = { note: MessageSquare, call: Phone, meeting: UsersIcon, stage_change: ArrowRightLeft, audit: History };

function AddActivity({ link, onAdded }) {
  const { api } = useWorkspace();
  const [form, setForm] = useState({ type: "note", subject: "", body: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/activities", { ...form, ...link }); setForm({ type: "note", subject: "", body: "" }); toast.success("Activity logged"); onAdded(); }
    catch (err) { toast.error(errorMessage(err)); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-2 rounded-lg border p-3" data-testid="add-activity-form">
      <div className="flex gap-2">
        <Select value={form.type} onValueChange={(v) => setForm((f) => ({ ...f, type: v }))}>
          <SelectTrigger className="w-36" data-testid="activity-type-select"><SelectValue /></SelectTrigger>
          <SelectContent>{ACTIVITY_TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
        </Select>
        <Input required placeholder="Subject" value={form.subject} onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))} data-testid="activity-subject-input" />
      </div>
      <Textarea rows={2} placeholder="Details (optional)" value={form.body} onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))} data-testid="activity-body-input" />
      <Button size="sm" type="submit" disabled={busy} data-testid="activity-submit-button">{busy && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}Log activity</Button>
    </form>
  );
}

export function Timeline({ activityParams, entityId, link, testid = "timeline" }) {
  const { can } = useWorkspace();
  const acts = useFetch("/activities", activityParams);
  const audit = useFetch(entityId ? "/audit-events" : null, { entity_id: entityId });
  const items = [
    ...(acts.data || []).map((a) => ({ id: a.id, kind: a.type, title: a.subject, body: a.body, at: a.occurred_at || a.created_at })),
    ...(audit.data || []).map((e) => ({ id: e.id, kind: "audit", title: `${e.action} ${e.entity_type}`, body: e.details?.changes ? `Changed: ${Object.keys(e.details.changes).join(", ")}` : null, at: e.created_at, actor: e.actor_email })),
  ].sort((a, b) => new Date(b.at) - new Date(a.at));
  const reload = () => { acts.reload(); audit.reload(); };

  return (
    <div className="space-y-4" data-testid={testid}>
      {can("write") && <AddActivity link={link} onAdded={reload} />}
      {acts.loading && !acts.data && <LoadingState />}
      {acts.error && <ErrorState message={acts.error} onRetry={reload} />}
      {acts.data && items.length === 0 && <p className="text-sm text-muted-foreground">No activity yet.</p>}
      <ol className="relative space-y-4 border-l border-border pl-5">
        {items.map((i) => {
          const Icon = ICONS[i.kind] || MessageSquare;
          return (
            <li key={`${i.kind}-${i.id}`} className="relative" data-testid={`${testid}-item-${i.id}`}>
              <span className="absolute -left-[29px] top-0.5 grid h-5 w-5 place-items-center rounded-full border bg-card"><Icon className="h-3 w-3 text-primary" /></span>
              <p className={`text-sm font-medium ${i.kind === "audit" ? "capitalize" : ""}`}>{i.title}</p>
              {i.body && <p className="mt-0.5 whitespace-pre-wrap text-sm text-muted-foreground">{i.body}</p>}
              <p className="mt-1 font-mono text-[11px] text-muted-foreground">{fmtDateTime(i.at)}{i.actor ? ` · ${i.actor}` : ""}</p>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
